<template>
  <div class="clinical-formulation-drug-exemptions">
    <div class="bg-light p-4 pt-2">
      <ExemptionsToggle
        :pmp-id="pmpId"
        :can-edit="canEdit"
        :exemptions-enabled="exemptionsEnabled"
        @error="showAlert($event, 'danger')"
        @success="showAlert($event, 'success')"
        class="mb-4"
      />

      <BRow class="mb-4 pt-2" align-v="center">
        <BCol cols="auto">
          <BFormInput
            v-model="searchQuery"
            @input="onSearchChange"
            type="text"
            placeholder="Search by GCN / Clinical Formulation ID..."
            aria-label="Search by GCN or Clinical Formulation ID"
            style="min-width: 350px;"
          />
        </BCol>
        <BCol class="text-end">
          <BButton v-if="canEdit" variant="primary" @click="openCreateModal">
            <i class="fas fa-plus me-2"></i>Add New Exemption
          </BButton>
        </BCol>
      </BRow>

      <BAlert v-if="alertMessage" :variant="alertVariant" :model-value="true" dismissible @dismissed="alertMessage = null" class="mb-3">
        {{ alertMessage }}
      </BAlert>

      <ExemptionsTable
        :items="exemptions"
        :fields="fields"
        :loading="loading"
        :current-page="currentPage"
        :per-page="perPage"
        :total-entries="totalEntries"
        :sort-field="sortField"
        :sort-order="sortOrder"
        @sort="sortBy"
        @page-change="currentPage = $event; fetchExemptions()"
        @per-page-change="perPage = $event; resetAndFetch()"
        @edit="openEditModal"
        @delete="deleteExemption"
      />

      <ExemptionsForm
        :show="showFormModal"
        :is-edit-mode="isEditMode"
        :initial-data="formData"
        :pmp-id="pmpId"
        @close="closeModal"
        @success="handleFormSuccess"
      />

      <ExemptionsDeleteModal
        :show="showDeleteModal"
        :target="deleteTarget"
        :loading="deleteLoading"
        @cancel="showDeleteModal = false"
        @confirm="confirmDelete"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { BRow, BCol, BButton, BFormInput, BAlert, vBTooltip } from 'bootstrap-vue-next';
import ExemptionsTable from './ClinicalFormulationDrugExemptionsTable.vue';
import ExemptionsForm from './ClinicalFormulationDrugExemptionsForm.vue';
import ExemptionsDeleteModal from './ClinicalFormulationDrugExemptionsDeleteModal.vue';
import ExemptionsToggle from './ClinicalFormulationDrugExemptionsToggle.vue';

export default {
  directives: {
    bTooltip: vBTooltip
  },
  components: {
    BRow,
    BCol,
    BButton,
    BFormInput,
    BAlert,
    ExemptionsTable,
    ExemptionsForm,
    ExemptionsDeleteModal,
    ExemptionsToggle
  },
  props: {
    pmpId: {
      type: Number,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    exemptionsEnabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      alertMessage: null,
      alertVariant: 'success',
      exemptions: [],
      currentPage: 1,
      perPage: 10,
      totalEntries: 0,
      sortField: 'clinical_formulation_id',
      sortOrder: 'asc',
      searchQuery: '',
      searchTimeout: null,
      showFormModal: false,
      showDeleteModal: false,
      isEditMode: false,
      deleteTarget: null,
      deleteLoading: false,
      formData: {
        clinical_formulation_id: '',
        age_cutoff: null
      },
      fields: [
        { key: 'clinical_formulation_id', label: 'GCN / Clinical Formulation ID' },
        { key: 'age_cutoff', label: 'Minimum Age to Report' },
        { key: 'created_at', label: 'Created At', formatter: (value) => this.formatDate(value) },
        { key: 'updated_at', label: 'Updated At', formatter: (value) => this.formatDate(value) }
      ]
    };
  },
  async mounted() {
    await this.fetchExemptions();
    if (this.canEdit) {
      this.fields.push({ key: 'actions', label: 'Actions', tdClass: 'text-center' });
    }
  },
  beforeUnmount() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = null;
  },
  methods: {
    async fetchExemptions() {
      this.loading = true;

      try {
        const response = await axios.get('/pmps/clinical_formulation_drug_exemptions.json', {
          params: {
            pmp_id: this.pmpId,
            page: this.currentPage,
            per_page: this.perPage,
            sort_by: this.sortField,
            sort_order: this.sortOrder,
            search: this.searchQuery
          }
        });

        this.exemptions = response.data.exemptions;
        this.totalEntries = response.data.total;
      } catch (err) {
        this.showAlert('Failed to load exemptions. Please try again.', 'danger');
      } finally {
        this.loading = false;
      }
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortField = field;
        this.sortOrder = 'asc';
      }
      this.resetAndFetch();
    },
    onSearchChange() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.resetAndFetch();
      }, 300);
    },
    resetAndFetch() {
      this.currentPage = 1;
      this.fetchExemptions();
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    },
    openCreateModal() {
      this.isEditMode = false;
      this.formData = { clinical_formulation_id: '', age_cutoff: null };
      this.showFormModal = true;
    },
    openEditModal(exemption) {
      this.isEditMode = true;
      this.formData = {
        id: exemption.id,
        clinical_formulation_id: exemption.clinical_formulation_id,
        age_cutoff: exemption.age_cutoff
      };
      this.showFormModal = true;
    },
    closeModal() {
      this.showFormModal = false;
      this.formData = { clinical_formulation_id: '', age_cutoff: null };
    },
    handleFormSuccess() {
      const msg = this.isEditMode ? 'Exemption updated successfully' : 'Exemption created successfully';
      this.showAlert(msg, 'success');
      this.resetAndFetch();
    },
    deleteExemption(exemptionId) {
      this.deleteTarget = this.exemptions.find(e => e.id === exemptionId);
      this.showDeleteModal = true;
    },
    showAlert(message, variant) {
      this.alertMessage = message;
      this.alertVariant = variant;
      if (variant === 'success') {
        setTimeout(() => { this.alertMessage = null; }, 4000);
      }
    },
    async confirmDelete() {
      this.deleteLoading = true;

      try {
        await axios.delete(`/pmps/clinical_formulation_drug_exemptions/${this.deleteTarget.id}?pmp_id=${this.pmpId}`);
        this.showAlert('Exemption deleted successfully', 'success');
        this.showDeleteModal = false;
        this.deleteTarget = null;
        this.resetAndFetch();
      } catch (err) {
        const msg = err.response?.data?.error?.[0] || 'Failed to delete exemption. Please try again.';
        this.showAlert(msg, 'danger');
      } finally {
        this.deleteLoading = false;
      }
    }
  }
};
</script>
